/**
 * FiltersBar — sidebar accordion filters with checkboxes
 * Each filter group is a collapsible section.
 * Bottom: "Применить" + "Очистить" buttons (full width).
 */

import { createElement, clearChildren } from '../utils/dom.js';
import { EVENTS } from '../core/events.js';

export default class FiltersBar {
  /**
   * @param {Object} eventBus
   * @param {Object} stateManager
   * @param {Object} i18n
   */
  constructor(eventBus, stateManager, i18n) {
    this._eventBus = eventBus;
    this._stateManager = stateManager;
    this._i18n = i18n;

    this._el = null;
    this._sectionsEl = null;
    this._actionsEl = null;

    // Pending filter selections (applied on "Применить" click)
    this._pending = {};
  }

  /**
   * Render the filters container
   * @param {HTMLElement} container - sidebar element
   */
  render(container) {
    this._el = createElement('div', 'grb-widget--filters');

    this._sectionsEl = createElement('div', 'grb-widget--filter-sections');
    this._el.appendChild(this._sectionsEl);

    // Action buttons
    this._actionsEl = createElement('div', 'grb-widget--filter-actions');
    this._actionsEl.style.display = 'none';

    const applyBtn = createElement('button', 'grb-widget--filter-apply-btn', {
      type: 'button',
    }, [this._i18n.t('filters.apply')]);
    applyBtn.addEventListener('click', () => this._applyFilters());

    const clearBtn = createElement('button', 'grb-widget--filter-clear-btn', {
      type: 'button',
    }, [this._i18n.t('filters.clear')]);
    clearBtn.addEventListener('click', () => {
      this._pending = {};
      this._eventBus.emit(EVENTS.FILTER_RESET);
    });

    this._actionsEl.appendChild(applyBtn);
    this._actionsEl.appendChild(clearBtn);
    this._el.appendChild(this._actionsEl);

    container.appendChild(this._el);
  }

  /**
   * Update filters UI from facets data
   * @param {Object} facets
   * @param {Object} activeFilters
   */
  update(facets, activeFilters) {
    if (!this._el || !facets) return;

    // Sync pending with active filters
    this._pending = { ...activeFilters };

    clearChildren(this._sectionsEl);

    const hasFacets = this._renderCategories(facets.categories || [], activeFilters.category_ids || [])
      | this._renderGenders(facets.genders || [], activeFilters.gender_ids || [])
      | this._renderColors(facets.colors || [], activeFilters.colors || [])
      | this._renderSizes(facets.sizes || [], activeFilters.sizes || [])
      | this._renderPrice(facets.price, activeFilters.price_min, activeFilters.price_max);

    // Show/hide action buttons
    this._actionsEl.style.display = hasFacets ? '' : 'none';
    this._el.style.display = hasFacets ? '' : 'none';
  }

  /** @private Create an accordion section */
  _createSection(title, isOpen) {
    const section = createElement('div', 'grb-widget--filter-section' + (isOpen ? ' grb-widget--open' : ''));

    const header = createElement('button', 'grb-widget--filter-header', { type: 'button' });
    const headerText = document.createTextNode(title);
    const chevron = createElement('span', 'grb-widget--filter-chevron');
    header.appendChild(headerText);
    header.appendChild(chevron);

    const body = createElement('div', 'grb-widget--filter-body');

    header.addEventListener('click', () => {
      const opening = !section.classList.contains('grb-widget--open');
      if (opening) {
        section.classList.add('grb-widget--open');
        body.style.maxHeight = body.scrollHeight + 'px';
      } else {
        body.style.maxHeight = body.scrollHeight + 'px';
        // Force reflow so browser registers the start value
        body.offsetHeight; // eslint-disable-line no-unused-expressions
        body.style.maxHeight = '0';
        section.classList.remove('grb-widget--open');
      }
    });

    section.appendChild(header);
    section.appendChild(body);

    // Call after appending to DOM to set initial max-height for open sections
    const finalize = () => {
      if (isOpen) {
        body.style.maxHeight = body.scrollHeight + 'px';
      }
    };

    return { section, body, finalize };
  }

  /** @private */
  _renderCategories(categories, active) {
    if (categories.length < 2) return false;

    const hasActive = active.length > 0;
    const { section, body, finalize } = this._createSection(this._i18n.t('filters.category'), hasActive);

    for (const cat of categories) {
      const isActive = active.includes(cat.id);
      const item = createElement('label', 'grb-widget--filter-item');

      const checkbox = createElement('input', '', { type: 'checkbox' });
      checkbox.checked = isActive;

      checkbox.addEventListener('change', () => {
        const current = this._pending.category_ids || [];
        if (checkbox.checked) {
          this._pending.category_ids = [...current, cat.id];
        } else {
          this._pending.category_ids = current.filter((id) => id !== cat.id);
        }
      });

      const label = createElement('span', 'grb-widget--filter-item-label', {}, [cat.name]);
      item.appendChild(checkbox);
      item.appendChild(label);
      if (cat.count != null) {
        item.appendChild(createElement('span', 'grb-widget--filter-item-count', {}, [String(cat.count)]));
      }
      body.appendChild(item);
    }

    this._sectionsEl.appendChild(section);
    finalize();
    return true;
  }

  /** @private */
  _renderGenders(genders, active) {
    if (genders.length < 2) return false;

    const hasActive = active.length > 0;
    const { section, body, finalize } = this._createSection(this._i18n.t('filters.gender'), hasActive);

    for (const g of genders) {
      const isActive = active.includes(g.id);
      const item = createElement('label', 'grb-widget--filter-item');

      const checkbox = createElement('input', '', {
        type: 'checkbox',
      });
      checkbox.checked = isActive;

      checkbox.addEventListener('change', () => {
        const current = this._pending.gender_ids || [];
        if (checkbox.checked) {
          this._pending.gender_ids = [...current, g.id];
        } else {
          this._pending.gender_ids = current.filter((id) => id !== g.id);
        }
      });

      const label = createElement('span', 'grb-widget--filter-item-label', {}, [g.name]);
      item.appendChild(checkbox);
      item.appendChild(label);
      if (g.count != null) {
        item.appendChild(createElement('span', 'grb-widget--filter-item-count', {}, [String(g.count)]));
      }
      body.appendChild(item);
    }

    this._sectionsEl.appendChild(section);
    finalize();
    return true;
  }

  /** @private */
  _renderColors(colors, active) {
    if (colors.length < 2) return false;

    const hasActive = active.length > 0;
    const { section, body, finalize } = this._createSection(this._i18n.t('filters.color'), hasActive);

    for (const c of colors) {
      const isActive = active.includes(c.value);
      const item = createElement('label', 'grb-widget--filter-item');

      const checkbox = createElement('input', '', {
        type: 'checkbox',
      });
      checkbox.checked = isActive;

      checkbox.addEventListener('change', () => {
        const current = this._pending.colors || [];
        if (checkbox.checked) {
          this._pending.colors = [...current, c.value];
        } else {
          this._pending.colors = current.filter((v) => v !== c.value);
        }
      });

      const dot = createElement('span', 'grb-widget--filter-color-dot', {
        style: { backgroundColor: c.hex_code || '#ccc' },
      });

      const label = createElement('span', 'grb-widget--filter-item-label', {}, [c.value]);
      item.appendChild(checkbox);
      item.appendChild(dot);
      item.appendChild(label);
      if (c.count != null) {
        item.appendChild(createElement('span', 'grb-widget--filter-item-count', {}, [String(c.count)]));
      }
      body.appendChild(item);
    }

    this._sectionsEl.appendChild(section);
    finalize();
    return true;
  }

  /** @private */
  _renderSizes(sizes, active) {
    if (sizes.length < 2) return false;

    const hasActive = active.length > 0;
    const { section, body, finalize } = this._createSection(this._i18n.t('filters.size'), hasActive);

    for (const s of sizes) {
      const isActive = active.includes(s.name);
      const item = createElement('label', 'grb-widget--filter-item');

      const checkbox = createElement('input', '', {
        type: 'checkbox',
      });
      checkbox.checked = isActive;

      checkbox.addEventListener('change', () => {
        const current = this._pending.sizes || [];
        if (checkbox.checked) {
          this._pending.sizes = [...current, s.name];
        } else {
          this._pending.sizes = current.filter((v) => v !== s.name);
        }
      });

      const label = createElement('span', 'grb-widget--filter-item-label', {}, [s.name]);
      item.appendChild(checkbox);
      item.appendChild(label);
      if (s.count != null) {
        item.appendChild(createElement('span', 'grb-widget--filter-item-count', {}, [String(s.count)]));
      }
      body.appendChild(item);
    }

    this._sectionsEl.appendChild(section);
    finalize();
    return true;
  }

  /** @private */
  _renderPrice(price, activeMin, activeMax) {
    if (!price) return false;

    const pMin = Math.floor(price.min);
    const pMax = Math.ceil(price.max);
    if (pMin >= pMax) return false;

    const hasActive = activeMin != null || activeMax != null;
    const { section, body, finalize } = this._createSection(this._i18n.t('filters.price'), hasActive);

    const wrapper = createElement('div', 'grb-widget--price-inputs');

    const minInput = createElement('input', 'grb-widget--price-input', {
      type: 'number',
      placeholder: `${this._i18n.t('filters.priceFrom')} ${pMin}`,
      min: String(pMin),
      max: String(pMax),
    });
    if (activeMin != null) minInput.value = activeMin;

    const maxInput = createElement('input', 'grb-widget--price-input', {
      type: 'number',
      placeholder: `${this._i18n.t('filters.priceTo')} ${pMax}`,
      min: String(pMin),
      max: String(pMax),
    });
    if (activeMax != null) maxInput.value = activeMax;

    const clampAndUpdate = () => {
      if (minInput.value !== '') {
        let v = Number(minInput.value);
        if (v < pMin) v = pMin;
        if (v > pMax) v = pMax;
        minInput.value = v;
      }
      if (maxInput.value !== '') {
        let v = Number(maxInput.value);
        if (v < pMin) v = pMin;
        if (v > pMax) v = pMax;
        maxInput.value = v;
      }
      // Ensure min <= max when both are set
      if (minInput.value !== '' && maxInput.value !== '' && Number(minInput.value) > Number(maxInput.value)) {
        const tmp = minInput.value;
        minInput.value = maxInput.value;
        maxInput.value = tmp;
      }
      this._pending.price_min = minInput.value ? Number(minInput.value) : null;
      this._pending.price_max = maxInput.value ? Number(maxInput.value) : null;
    };

    minInput.addEventListener('change', clampAndUpdate);
    maxInput.addEventListener('change', clampAndUpdate);

    wrapper.appendChild(minInput);
    wrapper.appendChild(createElement('span', 'grb-widget--price-separator', {}, ['—']));
    wrapper.appendChild(maxInput);
    body.appendChild(wrapper);

    this._sectionsEl.appendChild(section);
    finalize();
    return true;
  }

  /** @private Apply all pending filters at once */
  _applyFilters() {
    this._eventBus.emit(EVENTS.FILTER_CHANGE, this._pending);
  }

  hide() {
    if (this._el) this._el.style.display = 'none';
  }

  show() {
    if (this._el) this._el.style.display = '';
  }

  destroy() {
    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
    }
    this._el = null;
  }
}
